import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: any) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  theme: {
    color?: string;
  };
  modal: {
    ondismiss: () => void;
    confirm_close: boolean;
    animation: boolean;
  };
}

export const useRazorpay = () => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [failedPaymentData, setFailedPaymentData] = useState<{
    paymentId: string;
    orderId: string;
    orderData: any;
  } | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadRazorpayScript = (isMagicCheckout: boolean = false) => {
    return new Promise((resolve) => {
      // For Magic Checkout, we need the magic-checkout.js script
      const scriptUrl = isMagicCheckout 
        ? 'https://checkout.razorpay.com/v1/magic-checkout.js'
        : 'https://checkout.razorpay.com/v1/checkout.js';
      
      // Check if already loaded
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const createOrder = async (cartItems: any[], promoCode?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-razorpay-order', {
        body: { 
          cartItems: cartItems.map(item => ({
            id: item.id,
            quantity: item.quantity,
            name: item.name,
            variantInfo: item.variantInfo || null,
            price: item.price,
            image: item.image || null
          })),
          promoCode: promoCode || null
        }
      });

      if (error) {
        console.error('Order creation failed');
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error creating order');
      throw error;
    }
  };

  // Create Magic Checkout order with line_items for address/OTP flow
  const createMagicCheckoutOrder = async (cartItems: any[], promoCode?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-magic-checkout-order', {
        body: { 
          cartItems: cartItems.map(item => ({
            id: item.id,
            quantity: item.quantity,
            name: item.name,
            variantInfo: item.variantInfo || null,
            price: item.price,
            // Pass image from multiple possible sources
            image: item.thumbnail?.url || item.image || item.image_urls?.[0] || null,
            image_urls: item.image_urls || null
          })),
          promoCode: promoCode || null
        }
      });

      if (error) {
        console.error('Magic Checkout order creation failed');
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error creating Magic Checkout order');
      throw error;
    }
  };

  const verifyPayment = async (paymentData: any, orderData: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-razorpay-payment', {
        body: {
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_order_id: paymentData.razorpay_order_id,
          razorpay_signature: paymentData.razorpay_signature,
          orderData: {
            ...orderData,
            user_id: user?.id || null,
          }
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Payment verification error');
      throw error;
    }
  };

  const verifyPaymentWithRetry = async (
    paymentData: any, 
    orderData: any, 
    maxRetries = 3
  ): Promise<any> => {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        console.log(`Verification attempt ${attempt + 1}/${maxRetries}`);
        const result = await verifyPayment(paymentData, orderData);
        console.log('Verification successful on attempt', attempt + 1);
        return result;
      } catch (error) {
        lastError = error as Error;
        console.error(`Verification attempt ${attempt + 1} failed:`, error);
        
        // Exponential backoff: 1s, 2s, 4s
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    console.error('All verification attempts failed');
    throw lastError;
  };

  const checkPaymentStatus = async (razorpay_payment_id: string, razorpay_order_id: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('check-payment-status', {
        body: {
          razorpay_payment_id,
          razorpay_order_id,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error checking payment status:', error);
      throw error;
    }
  };

  const initiatePayment = async (orderData: any) => {
    setLoading(true);
    
    // Show immediate loading overlay
    const loadingOverlay = document.createElement('div');
    loadingOverlay.id = 'razorpay-loading-overlay';
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9998;
      backdrop-filter: blur(4px);
    `;
    
    const loadingContent = document.createElement('div');
    loadingContent.style.cssText = `
      background: white;
      padding: 3rem 4rem;
      border-radius: 1rem;
      text-align: center;
      max-width: 400px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    `;
    loadingContent.innerHTML = `
      <div style="margin-bottom: 1.5rem;">
        <svg width="60" height="60" viewBox="0 0 24 24" style="margin: 0 auto; display: block;">
          <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none" 
            stroke-dasharray="60" stroke-dashoffset="0" opacity="0.3"/>
          <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none"
            stroke-dasharray="60" stroke-dashoffset="15" 
            style="animation: rotate 1.5s linear infinite; transform-origin: center;">
            <animate attributeName="stroke-dashoffset" from="60" to="0" dur="1.5s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      <p style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.75rem; color: #1a1a1a;">Preparing Payment</p>
      <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 0;">Setting up secure payment gateway...</p>
      <style>
        @keyframes rotate {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    
    loadingOverlay.appendChild(loadingContent);
    document.body.appendChild(loadingOverlay);
    
    try {
      const isScriptLoaded = await loadRazorpayScript();
      
      if (!isScriptLoaded) {
        loadingOverlay?.remove();
        throw new Error('Failed to load Razorpay SDK');
      }

      const { data: keyData, error: keyError } = await supabase.functions.invoke('get-razorpay-key');
      
      if (keyError || !keyData?.key) {
        loadingOverlay?.remove();
        throw new Error('Failed to fetch payment configuration');
      }

      const orderResponse = await createOrder(
        orderData.items,
        orderData.promoCode
      );
      const order = orderResponse.order;
      const calculatedAmounts = orderResponse.calculatedAmounts;

      orderData.subtotal = calculatedAmounts.subtotal;
      orderData.shipping = calculatedAmounts.shipping;
      orderData.discount = calculatedAmounts.discount;
      orderData.total = calculatedAmounts.total;
      orderData.amount = calculatedAmounts.total;

      // Remove loading overlay before opening Razorpay
      loadingOverlay?.remove();

      // Store pending payment before opening Razorpay
      const pendingPayment = {
        razorpayOrderId: order.id,
        orderData,
        amount: calculatedAmounts.total,
        timestamp: Date.now()
      };
      localStorage.setItem('pendingPayment', JSON.stringify(pendingPayment));

      const options: RazorpayOptions = {
        key: keyData.key,
        amount: order.amount,
        currency: order.currency,
        name: 'Paudhify',
        description: 'Plant Purchase',
        order_id: order.id,
        handler: async (response: any) => {
          const overlay = document.createElement('div');
          overlay.id = 'payment-verification-overlay';
          overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.95);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            backdrop-filter: blur(4px);
          `;
          
          const loader = document.createElement('div');
          loader.style.cssText = `
            background: white;
            padding: 3rem 4rem;
            border-radius: 1rem;
            text-align: center;
            max-width: 400px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          `;
          loader.innerHTML = `
            <div style="margin-bottom: 1.5rem;">
              <svg width="60" height="60" viewBox="0 0 24 24" style="margin: 0 auto; display: block;">
                <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none" 
                  stroke-dasharray="60" stroke-dashoffset="0" opacity="0.3"/>
                <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none"
                  stroke-dasharray="60" stroke-dashoffset="15" 
                  style="animation: rotate 1.5s linear infinite; transform-origin: center;">
                  <animate attributeName="stroke-dashoffset" from="60" to="0" dur="1.5s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <p style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.75rem; color: #1a1a1a;">Verifying Payment</p>
            <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 0;">Please wait while we confirm your order...</p>
            <style>
              @keyframes rotate {
                to { transform: rotate(360deg); }
              }
            </style>
          `;
          
          overlay.appendChild(loader);
          document.body.appendChild(overlay);
          
          setVerifying(true);
          console.log('Payment completed, verifying...');

          try {
            // Automatic retry with exponential backoff
            const verificationResult = await verifyPaymentWithRetry(response, orderData);

            if (verificationResult.success) {
              // Clear pending payment on success
              localStorage.removeItem('pendingPayment');
              
              overlay?.remove();
              setVerifying(false);
              
              sessionStorage.setItem('orderData', JSON.stringify({
                orderId: verificationResult.order.id,
                items: orderData.items,
                subtotal: orderData.subtotal,
                shipping: orderData.shipping,
                discount: orderData.discount || 0,
                total: orderData.total,
                customerName: verificationResult.order.customer_name,
                createdAt: verificationResult.order.created_at,
                recovered: verificationResult.recovered || false
              }));

              navigate('/order-success', { replace: true });
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            // All retries failed - show recovery modal
            overlay?.remove();
            setVerifying(false);
            setLoading(false);
            
            console.error('Payment verification failed after retries', error);
            
            setFailedPaymentData({
              paymentId: response.razorpay_payment_id,
              orderId: response.razorpay_order_id,
              orderData
            });
            setShowRecoveryModal(true);
          }
        },
        prefill: {
          name: `${orderData.firstName || ''} ${orderData.lastName || ''}`.trim(),
          email: orderData.email || '',
          contact: orderData.phone || '',
        },
        theme: {},
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info('Payment cancelled');
          },
          confirm_close: true,
          animation: true,
        },
      };

      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response: any) {
        console.error('Payment failed');
        toast.error('Payment failed. Please try again.');
        setLoading(false);
      });

      rzp.open();
    } catch (error: any) {
      const loadingOverlay = document.getElementById('razorpay-loading-overlay');
      loadingOverlay?.remove();
      
      console.error('Payment initiation failed');
      toast.error(`Failed to initiate payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Express checkout - directly open Razorpay without full order data
  const initiateExpressPayment = async (cartItems: any[], promoCode?: string) => {
    setLoading(true);
    
    // Show loading overlay
    const loadingOverlay = document.createElement('div');
    loadingOverlay.id = 'razorpay-loading-overlay';
    loadingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9998;
      backdrop-filter: blur(4px);
    `;
    
    const loadingContent = document.createElement('div');
    loadingContent.style.cssText = `
      background: white;
      padding: 3rem 4rem;
      border-radius: 1rem;
      text-align: center;
      max-width: 400px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    `;
    loadingContent.innerHTML = `
      <div style="margin-bottom: 1.5rem;">
        <svg width="60" height="60" viewBox="0 0 24 24" style="margin: 0 auto; display: block;">
          <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none" 
            stroke-dasharray="60" stroke-dashoffset="0" opacity="0.3"/>
          <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none"
            stroke-dasharray="60" stroke-dashoffset="15" 
            style="animation: rotate 1.5s linear infinite; transform-origin: center;">
            <animate attributeName="stroke-dashoffset" from="60" to="0" dur="1.5s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>
      <p style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.75rem; color: #1a1a1a;">Starting Express Checkout</p>
      <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 0;">Opening Razorpay...</p>
      <style>
        @keyframes rotate {
          to { transform: rotate(360deg); }
        }
      </style>
    `;
    
    loadingOverlay.appendChild(loadingContent);
    document.body.appendChild(loadingOverlay);
    
    try {
      // Run all async operations in parallel for faster checkout
      const [isScriptLoaded, keyResult, orderResponse] = await Promise.all([
        loadRazorpayScript(true), // Load Magic Checkout script
        supabase.functions.invoke('get-razorpay-key'), // Fetch Razorpay key
        createMagicCheckoutOrder(cartItems, promoCode), // Create order
      ]);
      
      if (!isScriptLoaded) {
        loadingOverlay?.remove();
        throw new Error('Failed to load Razorpay Magic Checkout SDK');
      }

      const { data: keyData, error: keyError } = keyResult;
      if (keyError || !keyData?.key) {
        loadingOverlay?.remove();
        throw new Error('Failed to fetch payment configuration');
      }

      const order = orderResponse.order;
      const calculatedAmounts = orderResponse.calculatedAmounts;

      // Prepare minimal order data for express checkout
      const expressOrderData = {
        items: cartItems,
        subtotal: calculatedAmounts.subtotal,
        shipping: calculatedAmounts.shipping,
        discount: calculatedAmounts.discount,
        total: calculatedAmounts.total,
        amount: calculatedAmounts.total,
      };

      // Remove loading overlay before opening Razorpay
      loadingOverlay?.remove();

      // Store pending payment and set redirect flag
      const pendingPayment = {
        razorpayOrderId: order.id,
        orderData: expressOrderData,
        amount: calculatedAmounts.total,
        timestamp: Date.now(),
        isExpress: true
      };
      localStorage.setItem('pendingPayment', JSON.stringify(pendingPayment));
      localStorage.setItem('paymentInProgress', 'true');

      const options = {
        key: keyData.key,
        amount: order.amount,
        currency: order.currency,
        name: 'Paudhify',
        description: 'Plant Purchase',
        order_id: order.id,
        // Magic Checkout configuration
        one_click_checkout: true,
        show_coupons: true,
        prefill: {
          name: user?.email?.split('@')[0] || '',
          email: user?.email || '',
          contact: '',
        },
        theme: {},
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info('Payment cancelled');
          },
          confirm_close: true,
          animation: true,
        },
        enable_ga_analytics: true,
        // Handler to verify payment directly (same for all devices - no page reload)
        handler: (response: any) => {
            // Show loading overlay SYNCHRONOUSLY before any async operations
            // This prevents the flash of the background page
            const overlay = document.createElement('div');
            overlay.id = 'payment-verification-overlay';
            overlay.style.cssText = `
              position: fixed;
              inset: 0;
              background: rgb(0, 0, 0);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 99999;
            `;
            
            const loader = document.createElement('div');
            loader.style.cssText = `
              background: white;
              padding: 3rem 4rem;
              border-radius: 1rem;
              text-align: center;
              max-width: 400px;
              box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            `;
            loader.innerHTML = `
              <div style="margin-bottom: 1.5rem;">
                <svg width="60" height="60" viewBox="0 0 24 24" style="margin: 0 auto; display: block;">
                  <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none" 
                    stroke-dasharray="60" stroke-dashoffset="0" opacity="0.3"/>
                  <circle cx="12" cy="12" r="10" stroke="#10B981" stroke-width="2" fill="none"
                    stroke-dasharray="60" stroke-dashoffset="15" 
                    style="animation: rotate 1.5s linear infinite; transform-origin: center;">
                    <animate attributeName="stroke-dashoffset" from="60" to="0" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                </svg>
              </div>
              <p style="font-size: 1.25rem; font-weight: 600; margin-bottom: 0.75rem; color: #1a1a1a;">Verifying Payment</p>
              <p style="font-size: 0.875rem; color: #6b7280; margin-bottom: 0;">Please wait while we confirm your order...</p>
              <style>
                @keyframes rotate {
                  to { transform: rotate(360deg); }
                }
              </style>
            `;
            
            overlay.appendChild(loader);
            document.body.appendChild(overlay);
            
            // Force paint before async operations
            overlay.getBoundingClientRect();
            
            setVerifying(true);
            console.log('Express payment completed, verifying directly...');
            
            // Now run async verification
            (async () => {

            try {
              // Verify payment directly using express payment function (like standard checkout)
              const { data: verificationResult, error: verifyError } = await supabase.functions.invoke('verify-express-payment', {
                body: {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature || '',
                  cartItems: expressOrderData.items,
                  calculatedAmounts: {
                    subtotal: expressOrderData.subtotal,
                    shipping: expressOrderData.shipping,
                    discount: expressOrderData.discount,
                    total: expressOrderData.total
                  }
                }
              });

              if (verifyError) throw verifyError;

              if (verificationResult?.success) {
                // Clear pending payment on success
                localStorage.removeItem('pendingPayment');
                localStorage.removeItem('paymentInProgress');
                
                overlay?.remove();
                setVerifying(false);
                
                sessionStorage.setItem('orderData', JSON.stringify({
                  orderId: verificationResult.order.id,
                  items: expressOrderData.items,
                  subtotal: expressOrderData.subtotal,
                  shipping: expressOrderData.shipping,
                  discount: expressOrderData.discount || 0,
                  total: expressOrderData.total,
                  customerName: verificationResult.order.customer_name,
                  createdAt: verificationResult.order.created_at,
                  recovered: verificationResult.recovered || false
                }));

                // Navigate without page reload (like standard checkout)
                navigate('/order-success', { replace: true });
              } else {
                throw new Error('Payment verification failed');
              }
            } catch (error) {
              // Verification failed - show recovery modal
              overlay?.remove();
              setVerifying(false);
              setLoading(false);
              
              console.error('Express payment verification failed', error);
              
              setFailedPaymentData({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                orderData: expressOrderData
              });
              setShowRecoveryModal(true);
            }
            })();
        },
      };

      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response: any) {
        toast.error('Payment failed. Please try again.');
        setLoading(false);
      });

      rzp.open();
    } catch (error: any) {
      const loadingOverlay = document.getElementById('razorpay-loading-overlay');
      loadingOverlay?.remove();
      
      toast.error(`Failed to initiate payment: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryRetry = async () => {
    if (!failedPaymentData) return;
    
    setVerifying(true);
    
    try {
      // First check if order already exists
      const statusCheck = await checkPaymentStatus(
        failedPaymentData.paymentId,
        failedPaymentData.orderId
      );
      
      if (statusCheck.exists && statusCheck.order) {
        console.log('Order recovered successfully');
        localStorage.removeItem('pendingPayment');
        
        sessionStorage.setItem('orderData', JSON.stringify({
          orderId: statusCheck.order.id,
          items: failedPaymentData.orderData.items,
          subtotal: statusCheck.order.subtotal,
          shipping: statusCheck.order.shipping_cost,
          discount: statusCheck.order.discount_amount || 0,
          total: statusCheck.order.total_amount,
          customerName: statusCheck.order.customer_name,
          createdAt: statusCheck.order.created_at,
          recovered: true
        }));
        
        setShowRecoveryModal(false);
        navigate('/order-success', { replace: true });
        return;
      }
      
      // Order doesn't exist - try verification again
      const verificationResult = await verifyPaymentWithRetry(
        {
          razorpay_payment_id: failedPaymentData.paymentId,
          razorpay_order_id: failedPaymentData.orderId,
          razorpay_signature: ''
        },
        failedPaymentData.orderData
      );
      
      if (verificationResult.success) {
        localStorage.removeItem('pendingPayment');
        
        sessionStorage.setItem('orderData', JSON.stringify({
          orderId: verificationResult.order.id,
          items: failedPaymentData.orderData.items,
          subtotal: verificationResult.order.subtotal,
          shipping: verificationResult.order.shipping_cost,
          discount: verificationResult.order.discount_amount || 0,
          total: verificationResult.order.total_amount,
          customerName: verificationResult.order.customer_name,
          createdAt: verificationResult.order.created_at,
          recovered: true
        }));
        
        setShowRecoveryModal(false);
        navigate('/order-success', { replace: true });
      }
    } catch (error) {
      console.error('Recovery retry failed:', error);
      toast.error('Recovery failed. Please contact support with your payment ID.');
    } finally {
      setVerifying(false);
    }
  };

  const handleRecoveryCancel = () => {
    setShowRecoveryModal(false);
    setFailedPaymentData(null);
    navigate('/orders');
  };

  return { 
    initiatePayment,
    initiateExpressPayment,
    loading, 
    verifying,
    showRecoveryModal,
    failedPaymentData,
    handleRecoveryRetry,
    handleRecoveryCancel
  };
};
